import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Calendar, Tag, ArrowRight, Loader2, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  es: {
    tag: 'BLOG',
    title: 'Recursos y consejos para aprender español',
    subtitle: 'Artículos, guías y trucos para llevar tu español al siguiente nivel.',
    readMore: 'Leer artículo',
    empty: 'Aún no hay artículos publicados. ¡Vuelve pronto!',
    filterAll: 'Todos',
    filterEs: 'Español',
    filterEn: 'English',
    filterDe: 'Deutsch',
  },
  en: {
    tag: 'BLOG',
    title: 'Resources & tips to learn Spanish',
    subtitle: 'Articles, guides and tips to take your Spanish to the next level.',
    readMore: 'Read article',
    empty: 'No articles published yet. Come back soon!',
    filterAll: 'All',
    filterEs: 'Español',
    filterEn: 'English',
    filterDe: 'Deutsch',
  },
  de: {
    tag: 'BLOG',
    title: 'Ressourcen und Tipps zum Spanischlernen',
    subtitle: 'Artikel, Anleitungen und Tricks, um dein Spanisch zu verbessern.',
    readMore: 'Artikel lesen',
    empty: 'Noch keine Artikel veröffentlicht. Schau bald wieder rein!',
    filterAll: 'Alle',
    filterEs: 'Español',
    filterEn: 'English',
    filterDe: 'Deutsch',
  },
};

const LANG_BADGE_CLS = {
  es: 'bg-[#FFF0EF] text-[#E38987] border-[#F5A6A4]',
  en: 'bg-blue-50 text-blue-700 border-blue-200',
  de: 'bg-[#E8F5FA] text-[#5DA6C9] border-[#A8D8EA]',
};

export default function BlogPage() {
  const { language } = useLanguage();
  const lang = LABELS[language] ? language : 'es';
  const t = LABELS[lang];
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'es' | 'en' | 'de'

  useEffect(() => {
    document.title = `${t.title} · Spanisch en la Red`;
    // Fetch ALL published posts (all languages) and let the user filter
    axios.get(`${API}/blog`).then(({ data }) => {
      setPosts(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [t.title]);

  const filtered = useMemo(() => {
    if (activeFilter === 'all') return posts;
    return posts.filter((p) => p.language === activeFilter);
  }, [posts, activeFilter]);

  const availableLanguages = useMemo(() => {
    const set = new Set(posts.map((p) => p.language));
    return set;
  }, [posts]);

  const dateLocale = lang === 'es' ? 'es-ES' : lang === 'de' ? 'de-DE' : 'en-GB';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8F5FA] via-white to-[#FFF7F6]">
      <Seo path="/blog" title={t.title} description={t.subtitle} />
      <Navigation />
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <span className="text-[#F5A6A4] font-semibold text-sm uppercase tracking-wider">{t.tag}</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#4A4A4A] mt-3 mb-4">{t.title}</h1>
            <div className="w-20 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mx-auto mb-5" />
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">{t.subtitle}</p>
          </motion.div>

          {/* Language filter tabs - only shown when there are posts in >1 language */}
          {posts.length > 0 && availableLanguages.size > 1 && (
            <div className="flex items-center justify-center gap-2 flex-wrap mb-10" data-testid="blog-language-filter">
              {[
                { id: 'all', label: t.filterAll },
                { id: 'es', label: t.filterEs, showIf: availableLanguages.has('es') },
                { id: 'en', label: t.filterEn, showIf: availableLanguages.has('en') },
                { id: 'de', label: t.filterDe, showIf: availableLanguages.has('de') },
              ].filter((b) => b.id === 'all' || b.showIf).map((b) => (
                <button
                  key={b.id}
                  onClick={() => setActiveFilter(b.id)}
                  data-testid={`blog-filter-${b.id}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                    activeFilter === b.id
                      ? 'bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white shadow'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-[#A8D8EA]'
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-10 h-10 text-[#7DBDD9] animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-20" data-testid="blog-empty">{t.empty}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
              {filtered.map((post, i) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  whileHover={{ y: -4 }}
                  className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 transition-shadow flex flex-col"
                  data-testid={`blog-card-${post.slug}`}
                >
                  {post.cover_image && (
                    <div className="aspect-[16/9] overflow-hidden bg-[#E8F5FA]">
                      <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 flex-wrap text-xs text-gray-400 mb-2">
                      {post.published_at && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(post.published_at).toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase ${LANG_BADGE_CLS[post.language] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                        <Globe className="w-2.5 h-2.5" />{post.language}
                      </span>
                      {post.tags?.[0] && (
                        <span className="flex items-center gap-1 text-[#7DBDD9]">
                          <Tag className="w-3 h-3" />{post.tags[0]}
                        </span>
                      )}
                    </div>
                    <h2 className="text-xl font-bold text-[#4A4A4A] mb-3 leading-snug">{post.title}</h2>
                    <p className="text-gray-600 text-sm mb-5 flex-1">{post.excerpt}</p>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-1.5 text-[#7DBDD9] font-semibold text-sm hover:gap-2.5 transition-all self-start"
                    >
                      {t.readMore} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
